import { UndefinedInitialDataOptions, useQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { LoadedUser } from '../../interfaces';
import { getBusinessStripeAccount } from '../../services/business';
import { GetStripeAccountResponse } from '../../types/stripe';
import useGetOnboarding from '../businesses/useGetOnboarding';
import { isSudoManager } from '../../utils/global/helper';

type Options = Omit<
  UndefinedInitialDataOptions<
    GetStripeAccountResponse | null,
    Error,
    GetStripeAccountResponse | null,
    string[]
  >,
  'queryKey' | 'queryFn'
>;

function useGetStripeAccount(options?: Options) {
  const { user } = useUser<LoadedUser>();
  const { completedSteps, reloadAfterUpdate } = useGetOnboarding();

  const isEnabled =
    !!user?.business?.id && isSudoManager(user) && (options?.enabled ?? true);

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      'businessStripeAccount',
      user?.business?.id,
      user?.role,
      user?.canViewReports?.toString(),
    ],
    queryFn: async () => {
      if (!user?.business?.id) return null;
      const result = await getBusinessStripeAccount(user?.business?.id);

      // onboarding status was updated, reload the onboarding data
      const isPaymentOnboardingComplete = !!completedSteps?.some(
        step => step.id === 'PAYMENTS',
      );
      if (result.isOnboardComplete && !isPaymentOnboardingComplete) {
        reloadAfterUpdate('PAYMENTS');
      }

      return { ...result, businessId: user?.business?.id };
    },
    enabled: isEnabled,
    ...options,
  });

  return {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
}

export default useGetStripeAccount;
